/**
 * Process Skills Card
 * 
 * Displays skills required by a process in the chat interface
 */

import React, { useState, useEffect } from 'react';
import { List, Tag, Space, Empty, Spin, Badge } from 'antd';
import { CardSectionProps } from '../../../types/compositeCards';
import { apiClient } from '../../../services/apiClient';
import styles from './ProcessSkillsCard.module.css';

export const ProcessSkillsCard: React.FC<CardSectionProps> = ({ 
  card 
}) => {
  const { data } = card;
  
  // Initialize hooks first (before any conditional returns)
  const processId = 'processId' in data ? data.processId : null;
  const initialSkills = 'skills' in data ? data.skills : undefined;
  const [skills, setSkills] = useState(initialSkills || []);
  const [loading, setLoading] = useState(!initialSkills);

  useEffect(() => {
    if (!initialSkills && processId) {
      void loadSkills();
    }
  }, [processId]);
  
  // Type guard to ensure we have process skills data
  if (!('processId' in data)) {
    return null;
  }
  
  const { processName } = data;

  const loadSkills = async () => {
    if (!processId) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get(`/prime-processes/${processId}/skills`);
      setSkills(response.data);
    } catch (error) {
      console.error('Failed to load process skills:', error);
      setSkills([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.processSkillsCard}>
      <div className={styles.skillsHeader}>
        <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>
          Required Skills
        </h4>
        <Badge 
          count={skills.length} 
          style={{ backgroundColor: '#52c41a' }}
          showZero
        />
      </div>

      <Spin spinning={loading}>
        {skills.length > 0 ? (
          <List
            size="small"
            dataSource={skills}
            renderItem={(skill: any) => (
              <List.Item className={styles.skillListItem}>
                <List.Item.Meta
                  title={
                    <Space size="small">
                      <strong style={{ fontSize: '12px' }}>
                        {skill.skill_name || skill.name}
                      </strong>
                      {skill.skill_level !== undefined && (
                        <Tag color="green" style={{ fontSize: '10px', padding: '0 4px' }}>
                          Level {skill.skill_level}
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    skill.skill_description && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
                        {skill.skill_description}
                      </div>
                    )
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description="No skills required for this process"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '16px 0', fontSize: '11px' }}
          />
        )}
      </Spin>

      {skills.length > 0 && (
        <div className={styles.skillsSummary}>
          <Space size="small" style={{ fontSize: '11px', color: '#666' }}>
            <span>Total Skills Required:</span>
            <strong>{skills.length}</strong>
          </Space>
        </div>
      )}
    </div>
  );
};

